import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Image,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import ArticlesGrid, {
  ArticleHeader,
  specialPageRegularColumns,
} from '../../../components/ArticlesGrid';
import * as date from '../../../server/libs/date';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma from '../../../server/libs/prisma';
import withAuth from '../../../server/libs/withAuth';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import SpecialSideLoginBanner from '../../../components/SpecialSideLoginBanner';
import SideBarStickyContent from '../../../components/SideBarStickyContent';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import {
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isLicenseVerified,
} from '../../../common/libs/user';
import useSpecialPage from '../../../hooks/useSpecialPage';
import {
  findTagByTagName,
  getArticleIdsWithTagIds,
} from '../../../server/libs/tag';

const SEARCH_TARGET_TAG_NAME = '高カリウム血症';

const HYPERLALEMIA_PAGE_NAME = 'fec15a463248d6a9';
const specialPagePath = `/special/${HYPERLALEMIA_PAGE_NAME}`;

const HYPERLALEMIA_SURVEY_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLScj2LkDssJphNdVZzPf08I4uJFJNrowFxBX_CaX72DJkD-94g/viewform?usp=sf_link';

const specialPageSlug = HYPERLALEMIA_PAGE_NAME;
const specialPageShortName = 'hyperkalemia';
const mainImagePath = '/images/special/hyperkalemia_main_1300*720.png';
const specialPageTitle = '高カリウム血症特集';

const userAccessHistoryOperation = 'special_az';

type PageContext = {
  targetTag: Tag;
  hyperkalemiaSpecialArticles: ArticleHeader[];
  isMobile: boolean;
  isDemoAccount: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isPrivate: boolean;
};

type Tag = {
  tagId: number | null | undefined;
  name: string | null | undefined;
};

const HyperkalemiaPage: NextPage<PageContext> = ({
  targetTag,
  hyperkalemiaSpecialArticles,
  isMobile,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isDemoAccount,
  isPrivate,
}) => {
  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialPageShortName,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white">
        {breadcrumbs}
        <Grid
          templateRows={{ base: 'repeat(2, auto)', md: 'repeat(2, auto)' }}
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(12, 1fr)' }}
          gap={8}
          pr={[0, 0, 4]}
          pl={[0, 0, 4]}
        >
          {/* main start */}
          <GridItem
            colSpan={{ base: 1, md: 8 }}
            overflow={{ base: 'hidden', md: 'visible' }}
          >
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]} mb={4}>
              <Box pos="relative" mb={2}>
                <NextImage
                  src={mainImagePath}
                  alt="高カリウム血症特集"
                  width={1300}
                  height={720}
                />
              </Box>
              <Box
                p={{ base: 4, md: 6 }}
                borderRadius={5}
                borderColor="gray"
                borderWidth={1}
              >
                <Box mb={4}>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    企画概要
                  </Text>
                  <Text fontSize="sm">
                    重度になれば不整脈や心停止にもつながる高カリウム血症。
                  </Text>
                  <Text fontSize="sm">
                    昔の知識のままで診ていませんか？症状や基本的な対処法を知っていても原因精査や薬剤選択、治療の手順で悩むことも。
                  </Text>
                  <br />
                  <Text fontSize="sm">
                    こちらの特集ページでは急性/慢性の治療におけるポイントなど高カリウム血症に関するスライドを公開中！
                  </Text>
                  <Text fontSize="sm">
                    高カリウム血症についてみなさんの「知りたい」ことも募集中です。
                    <br />
                    <Link
                      isExternal
                      href={HYPERLALEMIA_SURVEY_URL}
                      onClick={() =>
                        sendAnalyticsEvent(
                          'special_contents',
                          'click',
                          'survey_text',
                        )
                      }
                    >
                      アンケート
                    </Link>
                    にもぜひご協力ください。
                  </Text>
                </Box>
                <Flex justifyContent="flex-end">
                  <NextLink href="https://slide.antaa.jp/docs/academy" passHref>
                    <Button
                      size="sm"
                      as="a"
                      onClick={() => {
                        sendAnalyticsEvent(
                          'special_contents',
                          'click',
                          'hyperkalemiaAcademy',
                        );
                      }}
                      textAlign="center"
                      display="inline-block"
                      color="brand.500"
                      borderColor="brand.500"
                      borderWidth={1}
                      bg="white"
                      p={4}
                      pl={{ base: 2, md: 4 }}
                      pr={{ base: 2, md: 4 }}
                      height="auto"
                      w={{ base: '50%', md: '30%' }}
                    >
                      投稿について学ぶ
                    </Button>
                  </NextLink>
                  <NextLink href="/article/upload?tags=高カリウム血症" passHref>
                    <Button
                      size="sm"
                      as="a"
                      color="white"
                      bg="brand.500"
                      onClick={() => {
                        sendAnalyticsEvent(
                          'special_contents',
                          'click',
                          'hyperkalemiaUpload2',
                        );
                      }}
                      textAlign="center"
                      display="inline-block"
                      p={2}
                      pl={{ base: 2, md: 4 }}
                      pr={{ base: 2, md: 4 }}
                      ml={4}
                      height="auto"
                      w={{ base: '50%', md: '30%' }}
                      _hover={{
                        opacity: '0.8',
                      }}
                    >
                      <Text as="span">#高カリウム血症</Text>
                      <br />
                      <Text as="span" fontSize="xs">
                        でスライド投稿
                      </Text>
                    </Button>
                  </NextLink>
                </Flex>
              </Box>
            </Box>
            {/* main end */}
            {/* 3rd start */}
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {hyperkalemiaSpecialArticles.length > 0 && (
                <Box pt={2} pb={2}>
                  <Flex justifyContent="space-between" mb={1}>
                    <Text fontWeight="bold" fontSize="md">
                      #高カリウム血症 に関するスライド
                    </Text>
                  </Flex>
                  <Flex direction={'column'}>
                    <ArticlesGrid
                      articles={hyperkalemiaSpecialArticles}
                      isMobile={isMobile}
                      gaEventLabel={'hyperkalemia_tagsearch'}
                      responsibleColumns={specialPageRegularColumns}
                      onShowThumbnail={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'show',
                          `special_hyperkalemia#${articleId}`,
                        );
                      }}
                      onClick={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'click',
                          `special_hyperkalemia#${articleId}`,
                        );
                      }}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
          </GridItem>
          {/* 3rd end */}
          {/* side start */}
          <GridItem colSpan={{ base: 1, md: 4 }} colStart={{ base: 1, md: 9 }}>
            <VStack spacing={4} pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {!isAuthenticated && (
                <SideBarStickyContent stickyHeight={45}>
                  <SpecialSideLoginBanner redirectUrl="/special/fec15a463248d6a9" />
                </SideBarStickyContent>
              )}
              {isAuthenticated && isLicenseVerified && (
                <Box _hover={{ opacity: '0.8' }}>
                  <Link
                    isExternal
                    href={HYPERLALEMIA_SURVEY_URL}
                    onClick={() =>
                      sendAnalyticsEvent('special_contents', 'click', 'survey')
                    }
                  >
                    <Image
                      src="/images/special/hyperkalemia_survey_720*618.png"
                      alt="みなさんの「知りたい」を募集中"
                      width={720}
                      height="auto"
                    />
                  </Link>
                </Box>
              )}
            </VStack>
          </GridItem>
          {/* side end */}
        </Grid>
      </Box>
    </>
  );
};

export default HyperkalemiaPage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const { isPrivate, canDisplay } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    let hyperkalemiaSpecialArticles: ArticleHeader[] = [];
    const targetTag = await findTagByTagName(SEARCH_TARGET_TAG_NAME);
    if (targetTag) {
      const articleIdList: string[] = await getArticleIdsWithTagIds(
        targetTag ? [targetTag.tag_id] : [],
      );

      const articleHeaders = await prisma.article_header.findMany({
        select: {
          article_id: true,
          name: true,
          article_type: true,
          status: true,
          top_img_m: true,
          session_count: true,
          like_count: true,
          disclose_scope: true,
          create_date: true,
          update_date: true,
          user_article_header_author_idTouser: true,
          article_tag_connection: {
            select: {
              tag: true,
            },
          },
          article_page: {
            select: {
              like_count: true,
            },
          },
          article_comments: {
            select: {
              id: true,
            },
          },
        },
        where: {
          article_id: {
            in: articleIdList,
          },
          status: 'DONE',
          hidden: false,
          is_deleted: false,
        },
        orderBy: {
          create_date: 'desc',
        },
      });
      // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
      const discloseIds = articleHeaders
        .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
        .filter((v) => !!v);
      const idBaseProfileMap = await fetchAccountIntegrationProfiles(
        discloseIds,
      );

      hyperkalemiaSpecialArticles = articleHeaders.reduce(
        (articles: any, article: any) => {
          const author = article.user_article_header_author_idTouser;
          const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

          if (
            shouldNotShow(
              idBaseProfile,
              context.req.user,
              context.req.isAuthenticated(),
              article,
            )
          ) {
            return articles;
          }

          articles.push({
            articleId: article.article_id,
            name: article.name,
            articleType: article.article_type,
            status: article.status as ArticleStatus | null,
            topImageM: article.top_img_m,
            sessionCount: article.session_count || 0,
            likeCount:
              article.like_count +
              article.article_page.reduce(
                (prev: any, curr: any) => prev + curr.like_count,
                0,
              ),
            commentCount: article.article_comments.length,
            discloseScope: article.disclose_scope as DiscloseScope | null,
            createDate: date.toIso8601Jst(article.create_date),
            updateDate: date.toIso8601Jst(article.update_date),
            author: {
              userId: article.user_article_header_author_idTouser.user_id,
              userName: idBaseProfile?.userName ?? null,
              // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
              lastName: idBaseProfile?.userName
                ? null
                : idBaseProfile?.lastName ?? author.last_name,
              firstName: idBaseProfile?.userName
                ? null
                : idBaseProfile?.firstName ?? author.first_name,
              updateDateEpochMilliseconds:
                article.user_article_header_author_idTouser.update_date.getTime(),
              profilePhotoS3Key:
                article.user_article_header_author_idTouser
                  .profile_photo_s3_key,
              idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
              antaaAccountType: idBaseProfile?.accountType ?? null,
              antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
              isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
            },
            tags: article.article_tag_connection.map((conn: any) => ({
              id: conn.tag.tag_id,
              name: conn.tag.tag_name,
            })),
          });
          return articles;
        },
        [],
      );
    }

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    return {
      props: {
        targetTag: {
          tagId: targetTag?.tag_id || null,
          name: targetTag?.tag_name || null,
        },
        hyperkalemiaSpecialArticles,
        isMobile,
        isDemoAccount,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isPrivate,
      },
    };
  },
);
